import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TablePagination,
  Box,
} from '@mui/material';
import {ArrowDownward, ArrowUpward} from '@mui/icons-material';

export interface ICustomTableColumn<T> {
  key: keyof T | string;
  label: string;
  isBold?: boolean;
  align?: 'left' | 'center' | 'right';
  className?: string;
  render?: (value: T[keyof T] | undefined, row: T) => React.ReactNode;
  isSortable?: boolean;
}

interface ICustomTableProps<T extends Record<string, unknown>> {
  columns: ICustomTableColumn<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  className?: string;
  pageSize?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  totalCount?: number;
  onSortChange?: (key: string, direction: 'asc' | 'desc') => void;
  compact?: boolean;
  showPagination?: boolean;
  loading?: boolean;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
}

const LoadingSkeletonRow = ({
  columns,
}: {
  columns: ICustomTableColumn<Record<string, unknown>>[];
}) => (
  <TableRow sx={{borderBottom: 'none'}}>
    {columns.map((_, colIndex) => (
      <TableCell key={colIndex} sx={{borderBottom: 'none'}} className="py-4">
        <Box
          sx={{
            height: 16,
            background:
              'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'loading-shimmer 1.5s infinite',
            borderRadius: 1,
            width: '100%',
            maxWidth: 120,
          }}
        />
      </TableCell>
    ))}
  </TableRow>
);

const CustomTable = <T extends Record<string, unknown>>({
  columns,
  data,
  onRowClick,
  className = '',
  pageSize = 10,
  currentPage = 0,
  onPageChange,
  totalCount = 0,
  onSortChange,
  sortBy,
  sortDirection,
  compact = false,
  showPagination = true,
  loading = false,
}: ICustomTableProps<T>) => {
  const handleSort = (key: string) => {
    if (!onSortChange) return;

    let direction: 'asc' | 'desc' = 'asc';
    if (sortBy === key && sortDirection === 'asc') {
      direction = 'desc';
    }

    onSortChange(key, direction);
  };

  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    onPageChange?.(newPage);
  };

  const getTableColumns = () => {
    return (
      <>
        {columns.map((col, index) => {
          const isSorted = sortBy === col.key;
          const arrow =
            sortDirection === 'asc' ? (
              <ArrowUpward fontSize="small" className="text-purple-600" />
            ) : (
              <ArrowDownward fontSize="small" className="text-purple-600" />
            );

          return (
            <TableCell
              key={index}
              align={col.align || 'left'}
              className={`${col.className ?? ''}`}
              onClick={() => col.isSortable && handleSort(col.key.toString())}
              sx={{backgroundColor: 'transparent'}}
            >
              <div
                className={`flex items-center gap-1 ${
                  col.isSortable ? 'cursor-pointer select-none' : ''
                }${col.align === 'center' ? ' justify-center' : ''}`}
              >
                {col.label}
                {col.isSortable &&
                  (isSorted ? (
                    arrow
                  ) : (
                    <ArrowDownward
                      fontSize="small"
                      className="text-purple-600 opacity-40"
                    />
                  ))}
              </div>
            </TableCell>
          );
        })}
      </>
    );
  };

  return (
    <Paper className={`bg-transparent rounded-xl p-2 shadow-sm ${className}`}>
      <TableContainer
        sx={{
          overflowY: 'visible',
          '& .MuiTable-root': {
            tableLayout: 'fixed',
          },
          '& .MuiTableHead-root': {
            position: 'sticky',
            top: 0,
            zIndex: 2,
          },
          '& .MuiTableBody-root': {
            overflow: 'visible',
            '& tr:first-of-type': {
              '& td': {
                paddingTop: '28px',
              },
            },
          },
          '& .MuiTableCell-root': {
            padding: '6px 12px',
          },
          '@keyframes loading-shimmer': {
            '0%': {
              backgroundPosition: '-200% 0',
            },
            '100%': {
              backgroundPosition: '200% 0',
            },
          },
        }}
        className="no-scrollbar"
      >
        <Table size={compact ? 'small' : 'medium'} stickyHeader>
          <TableHead>
            <TableRow sx={{borderBottom: 'none'}}>{getTableColumns()}</TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              // Show skeleton rows to maintain table height
              Array.from({length: pageSize}, (_, index) => (
                <LoadingSkeletonRow
                  key={`skeleton-${index}`}
                  columns={
                    columns as ICustomTableColumn<Record<string, unknown>>[]
                  }
                />
              ))
            ) : data.length > 0 ? (
              data.map((row, rowIndex) => (
                <TableRow
                  key={rowIndex}
                  onClick={() => onRowClick?.(row)}
                  className="cursor-pointer"
                  sx={{borderBottom: 'none'}}
                >
                  {columns.map((col, colIndex) => {
                    const rawValue = row[col.key as keyof T];
                    return (
                      <TableCell
                        key={colIndex}
                        align={col.align || 'left'}
                        className={col.className}
                        sx={{borderBottom: 'none'}}
                      >
                        <Typography fontWeight={col.isBold ? 'bold' : 'normal'}>
                          {col.render
                            ? col.render(rawValue, row)
                            : String(rawValue ?? '')}
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow sx={{borderBottom: 'none'}}>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  className="text-purple-600 py-4"
                  sx={{borderBottom: 'none'}}
                >
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && !loading && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '16px 16px 8px 16px',
            zIndex: 1000,
          }}
        >
          <TablePagination
            component="div"
            count={totalCount}
            page={currentPage}
            onPageChange={handleChangePage}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[]}
            className="text-sm"
          />
        </Box>
      )}
    </Paper>
  );
};

export default CustomTable;
